<script setup>
import { ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import ResultsV2CardWrapper from "./Cards/ResultsV2CardWrapper.vue";
import { EmptyState } from "@/components/episto-ui/empty-state";
import { LoadingState } from "@/components/episto-ui/loading-state";
import { revealAndScrollToCard } from "@/utils/scrollCardIntoView";
import { isHalfWidthCard } from "./constants";

const store = useResultsV2Store();
const cardsStore = useResultsV2CardsStore();
const { loading, editMode, viewLayout, printTallCardIds } = storeToRefs(store);
const { cards, lastAddedCardId, collapsedSectionIds, sectionIdByCardId } = storeToRefs(cardsStore);

const listEl = ref(null);

const displayedCards = computed(() =>
  cards.value.filter((card) => {
    const sectionId = sectionIdByCardId.value[card.id];
    return sectionId == null || !collapsedSectionIds.value.has(sectionId);
  }),
);

const halfCardIds = computed(() => {
  const ids = new Set();
  if (editMode.value) return ids;
  let run = [];
  const flush = () => {
    if (run.length % 2 === 1 && !run.at(-1).options?.width) run.pop();
    run.forEach((card) => ids.add(card.id));
    run = [];
  };
  for (const card of displayedCards.value) {
    if (isHalfWidthCard(card, viewLayout.value)) run.push(card);
    else flush();
  }
  flush();
  return ids;
});

function cardWidthClass(card) {
  return halfCardIds.value.has(card.id) && !printTallCardIds.value.has(card.id) ? "w-[calc(50%-0.5rem)]" : "w-full";
}

watch(
  lastAddedCardId,
  (cardId) => {
    if (cardId != null) revealAndScrollToCard(cardId);
  },
  { flush: "post" },
);
</script>

<template>
  <div ref="listEl" data-results-card-list class="relative flex flex-wrap items-stretch gap-4 px-1 pb-8 print:pb-0">
    <!-- Cards -->
    <LoadingState v-if="loading" class="w-full" />
    <!-- Section collapse/expand: cards fade in place; leaving cards drop out of the flow
         (absolute) so siblings reflow smoothly via the move transition. -->
    <TransitionGroup
      v-else
      move-class="transition-transform duration-500 ease-[cubic-bezier(0.25,0.8,0.25,1)]"
      enter-active-class="transition duration-500 ease-out"
      enter-from-class="opacity-0 translate-y-3"
      leave-active-class="absolute transition duration-200 ease-in"
      leave-to-class="opacity-0 -translate-y-2 scale-[0.98]">
      <ResultsV2CardWrapper
        v-for="card in displayedCards"
        :key="card.id"
        :card="card"
        :class="cardWidthClass(card)"
        :readonly="!editMode" />
    </TransitionGroup>
    <EmptyState v-if="!loading && !cards.length" title="No cards in this dashboard" size="lg" class="w-full" />
    <!-- Permanent spacer so the last card can always be scrolled to the top -->
    <div class="w-full shrink-0 h-screen print:hidden" aria-hidden="true" />
  </div>
</template>
